import React, { useContext } from 'react'
import { Modal, View } from 'oio-react'
import Helmet from 'react-helmet'
import { useParams, useRouteMatch } from 'react-router-dom'
import { LAYOUT_BOX, LAYOUT_HEADER, LAYOUT_SIDEBAR } from 'config/constants/ui'
import AccountSettings from 'src/apps/Account/apps/Settings'
import BanUserConfirm from 'src/apps/Account/components/BanUserConfirm'
import NotFound from 'src/apps/Global/apps/NotFound'
import SendMessage from 'src/apps/Messages/apps/SendMessage'
import ModalRoute from 'src/components/utils/ModalRoute'
import { useOrganization, useUser } from 'src/graphql/hooks'
import ProtoThemeContext from 'src/libraries/contexts/ProtoTheme'
import Collection from '~/apps/Collection'
import LoginHistory from '~/apps/LoginHistory'
import AboutBlock from '~/components/AboutBlock'
import CollectionBlock from '~/components/CollectionBlock'
import RecentsBlock from '~/components/RecentsBlock'
import SpaceBlock from '~/components/SpaceBlock'

const Profile = () => {
   const { userId } = useParams()
   const match = useRouteMatch()
   const { protoTheme: theme } = useContext(ProtoThemeContext)
   const isBoxGL = theme.tmpGlobalLayout === LAYOUT_BOX
   const isHeaderGL = theme.tmpGlobalLayout === LAYOUT_HEADER
   const isSidebarGL = theme.tmpGlobalLayout === LAYOUT_SIDEBAR

   const containerPaddingLeft = isHeaderGL
      ? theme.tmpViewportPadding
      : `
         ${theme.tmpViewportPaddingA}[a]
         ${theme.tmpViewportPaddingB}[b]
         ${theme.tmpViewportPaddingC}[c]
      `

   const containerPaddingRight = isSidebarGL
      ? `
         ${theme.tmpViewportPaddingA}[a]
         ${theme.tmpViewportPaddingB}[b]
         ${theme.tmpViewportPaddingC}[c]
      `
      : theme.tmpViewportPadding

   const { organization } = useOrganization()
   const { user, error, networkStatus } = useUser({ id: userId })
   const initialLoading = networkStatus <= 2

   if (initialLoading) {
      return null
   }

   if (error) {
      return <NotFound />
   }

   return (
      <>
         <Helmet title={user.fullName} />
         <View
            float="left"
            display="flex[d-f]"
            justifyContent="center"
            width="100%"
            backgroundColor={`${theme.tmpContentBackgroundColor}[a-c]`}>
            <View
               display={isBoxGL ? 'block' : 'block[a-d] flex[d-f]'}
               width="100%"
               paddingLeft={containerPaddingLeft}
               paddingRight={containerPaddingRight}>
               <View
                  flex="0 0 auto"
                  width={isBoxGL ? '100%' : '100%[a-c] 370px[d] 400px[e] 400px[f]'}
                  backgroundColor={theme.tmpContentBackgroundColor}>
                  <View paddingHorizontal="30px[d-f]">
                     <AboutBlock />
                  </View>
               </View>
               <View flex="1 1 auto" height="100%">
                  <View
                     float="left"
                     width="100%"
                     height="100%"
                     paddingTop={theme.tmpSpacing}
                     paddingLeft="30px[d-f]"
                     paddingRight="30px[d-f]"
                     borderTop={isBoxGL
                        ? `1px solid ${theme.tmpContentLineColor}`
                        : `1px solid ${theme.tmpContentLineColor}[a-c]`
                     }
                     borderLeft={!isBoxGL && `1px solid ${theme.tmpColumnBorderColor}[d-f]`}
                     backgroundColor={theme.tmpContentBackgroundColor}>
                     <CollectionBlock />
                     <RecentsBlock />
                     {userId === 'me' && <SpaceBlock />}
                  </View>
               </View>
            </View>
         </View>
         <ModalRoute path={`${match.path}/collection/:collectionFilter`}>
            {({ open, onCloseComplete, onCloseTrigger }) => (
               <Modal
                  borderRadius="5px"
                  width="100%[a] 360px[b] 90%[c] 650px[d-f]"
                  height="100%[a] 80%[b] 90%[c-f]"
                  onCloseComplete={onCloseComplete}
                  onCloseTrigger={onCloseTrigger}
                  open={open}>
                  <Collection onCloseButtonClick={onCloseTrigger} />
               </Modal>
            )}
         </ModalRoute>
         {organization.currentUserCanEdit && (
            <ModalRoute path={`${match.path}/ban-user`}>
               {({ open, onCloseComplete, onCloseTrigger }) => (
                  <Modal
                     borderRadius="6px"
                     width="100%[a-b] 400px[c-f]"
                     maxHeight="100%"
                     onCloseComplete={onCloseComplete}
                     onCloseTrigger={onCloseTrigger}
                     open={open}>
                     <BanUserConfirm
                        onBanComplete={() => window.location.assign('/')}
                        onCancelButtonClick={onCloseTrigger}
                        userId={userId}
                     />
                  </Modal>
               )}
            </ModalRoute>
         )}
         {userId !== 'me' && (
            <ModalRoute path={`${match.path}/send-message`}>
               {({ open, onCloseComplete, onCloseTrigger }) => (
                  <Modal
                     borderRadius="6px"
                     width="90%[a-b] 500px[c-f]"
                     maxHeight="100%"
                     onCloseComplete={onCloseComplete}
                     onCloseTrigger={onCloseTrigger}
                     open={open}>
                     <SendMessage
                        onCloseButtonClick={onCloseTrigger}
                        onSend={onCloseTrigger}
                        recipientFirstName={user.firstName}
                     />
                  </Modal>
               )}
            </ModalRoute>
         )}
         {userId === 'me' && (
            <>
               <ModalRoute path={`${match.path}/settings`}>
                  {({ open, onCloseComplete, onCloseTrigger }) => (
                     <Modal
                        borderRadius="5px"
                        width="100%[a] 360px[b] 90%[c] 720px[d-f]"
                        height="100%[a] 80%[b] 570px[c-f]"
                        onCloseComplete={onCloseComplete}
                        onCloseTrigger={onCloseTrigger}
                        open={open}>
                        <AccountSettings onCloseButtonClick={onCloseTrigger} />
                     </Modal>
                  )}
               </ModalRoute>
               <ModalRoute path={`${match.path}/login-history`}>
                  {({ open, onCloseComplete, onCloseTrigger }) => (
                     <Modal
                        borderRadius="5px"
                        width="100%"
                        height="100%[a-b] 95%[c-f]"
                        maxWidth="650px"
                        onCloseComplete={onCloseComplete}
                        onCloseTrigger={onCloseTrigger}
                        open={open}>
                        <LoginHistory onCloseButtonClick={onCloseTrigger} />
                     </Modal>
                  )}
               </ModalRoute>
            </>
         )}
      </>
   )
}

export default Profile
